import React from 'react';
import { FormattedMessage } from 'react-intl';
import classnames from 'classnames';
import { useSelector } from 'react-redux';
import { useNonogramSubmitAnswer, NonogramPuzzle, getUserInfo } from '@games/api';
import messages from 'intl/messages';
import moment from 'moment';
import MyLink from 'features/home/components/MyLink';
import { DailyTopTimes } from 'features/home/components/DailyTopTimes';
import Loader from './Loader';
import Timer from './Timer';
import Puzzle from './Puzzle';
import {
  PuzzleCellState,
  resetPuzzle,
  selectHighlight,
  selectPuzzleAnswer,
  selectSolved,
  selectTimerStart,
  setHighlight,
} from '../../../redux/puzzle-slice';
import { useAppDispatch } from '../../../redux/store';
import { useSendEvent } from '../../../utils/events';

interface PuzzleWrapperProps {
  loading: boolean;
  error?: any;
  puzzle?: NonogramPuzzle;
  isDaily?: boolean;
  date?: string;
}

type Message = 'goodLuck' | 'correct' | 'errorFound' | 'playSudokuDailyToday' | 'playNonogramDailyToday';

const PuzzleWrapper: React.FC<PuzzleWrapperProps> = ({
  loading,
  error,
  puzzle,
  isDaily = false,
  date,
}) => {
  const [message, setMessage] = React.useState<Message>('goodLuck');
  const highlight = useSelector(selectHighlight);
  const [solvedSent, setSolvedSent] = React.useState<boolean>(false);
  const solved = useSelector(selectSolved);
  const dispatch = useAppDispatch();
  const sendEvent = useSendEvent();
  const timerStart = useSelector(selectTimerStart);
  const puzzleAnswer = useSelector(selectPuzzleAnswer);
  const [{ data }, submitAnswer] = useNonogramSubmitAnswer();
  const userInfo = getUserInfo();

  const toggleHighlight = React.useCallback(() => {
    dispatch(setHighlight(!highlight));
  }, [dispatch, highlight]);

  const startOver = React.useCallback(() => {
    sendEvent('reset');
    setMessage('goodLuck');
    dispatch(resetPuzzle());
  }, [dispatch, sendEvent]);

  React.useEffect(() => {
    if (solved && !solvedSent && puzzle) {
      setSolvedSent(true);
      sendEvent(`winner${highlight ? '-highlight' : ''}`);

      if (!userInfo) {
        setMessage('correct');
        return;
      }

      if (userInfo.nonogramLastDailyDate !== new Date().toISOString().split('T')[0] && !isDaily) {
        setMessage('playNonogramDailyToday');
      } else if (userInfo.sudokuLastDailyDate !== new Date().toISOString().split('T')[0]) {
        setMessage('playSudokuDailyToday');
      } else {
        setMessage('correct');
      }

      const answer: string[] = [];
      for (let i = 0; i < puzzle.size; i++) {
        let row = '';
        for (let j = 0; j < puzzle.size; j++) {
          row += puzzleAnswer[j][i] === PuzzleCellState.On ? '1' : '0';
        }
        answer.push(row);
      }
      submitAnswer({
        data: {
          hash: puzzle?.hash || '',
          duration: Math.floor((Date.now() - timerStart) / 1000),
          answer,
        }
      });
    }
  }, [
    dispatch,
    highlight,
    timerStart,
    solved,
    solvedSent,
    sendEvent,
    submitAnswer,
  ]);

  const newNonogram = React.useCallback(() => {
    sendEvent('new-puzzle');
    if (isDaily) {
      window.location.href = '/';
      return;
    }
    window.location.reload();
  }, [sendEvent, isDaily]);

  if (loading) {
    return (
      <div>
        <h1>
          <FormattedMessage {...messages.loading} />
        </h1>
        <Loader />
      </div>
    );
  }

  if (error || !puzzle) {
    return (
      <div>
        <FormattedMessage {...messages.ajaxError} />
      </div>
    );
  }

  return (
    <div className='text-center'>
      <h1 className='text-3xl font-bold my-3'>
        {isDaily ? (
          <MyLink href={`/daily/top/${date ? date : ''}`}>
            <FormattedMessage {...messages.dailyNonogramFor} />
            {moment(date).format('MMM D, YYYY')}
          </MyLink>
        ) : (
          <FormattedMessage {...messages.nonogramPuzzle} />
        )}
      </h1>
      <div>
        {!isDaily && (
          <>
            <FormattedMessage {...messages.nonogram} /> #
            <MyLink href={`/nonogram/puzzle/${puzzle.id}`}>
              {puzzle.id}
            </MyLink>
          </>
        )}
      </div>
      <Timer />
      <div className='my-2 text-lg'>
        {(message === 'correct' || message === 'goodLuck') && (
          <FormattedMessage {...messages[message]} />
        )}
        {message === 'playSudokuDailyToday' && (
          <MyLink
            href='https://sudokucore.com/daily?utm_source=nonogram&utm_medium=dailycrosslink'
            style={{ textDecoration: 'underline' }}
            useATag
          >
            <FormattedMessage {...messages.playSudokuDailyToday} />
          </MyLink>
        )}
        {message === 'playNonogramDailyToday' && (
          <MyLink
            href='/daily'
            style={{ textDecoration: 'underline' }}
          >
            <FormattedMessage {...messages.playNonogramDailyToday} />
          </MyLink>
        )}
      </div>
      {data?.success && (
        <div className='my-2 text-lg text-green-600'>
          <FormattedMessage
            {...messages.timeRecorded}
          />
        </div>
      )}
      <div>
        <Puzzle />
      </div>
      <div className='mt-2 flex flex-row justify-center mt-2 gap-3'>
        <button
          className='btn btn-secondary'
          onClick={startOver}
        >
          <FormattedMessage {...messages.startOver} />
        </button>
        <button
          className='btn btn-secondary'
          onClick={newNonogram}
        >
          <FormattedMessage {...messages.newNonogram} />
        </button>
        <button
          className={classnames(
            'btn',
            highlight ? 'btn-success' : 'btn-danger',
          )}
          onClick={toggleHighlight}
        >
          <input
            id="show-correct-checkbox"
            type='checkbox'
            checked={highlight}
            onChange={toggleHighlight}
          />
          <label htmlFor="show-correct-checkbox" className='ml-2'>
            <FormattedMessage {...messages.showCorrect} />
          </label>
        </button>
      </div>
      {isDaily && (
        <div className='mt-4'>
          <DailyTopTimes date={date || ''} />
        </div>
      )}
    </div>
  );
};

export default PuzzleWrapper;
